import { useRef } from "react";
import { getSport } from "@/sports";
import { ROUTE_STYLES, BALL_STYLES, textOn } from "@/lib/playStyles";
import { playerPosAt, ballAt, routeTiming, playerById, sortedEvents } from "@/lib/playEngine";

const ptsStr = (pts) => pts.map((p) => `${p.x},${p.y}`).join(" ");

export function RouteDefs() {
  const arrows = { run: ROUTE_STYLES.run.color, motion: ROUTE_STYLES.motion.color, pass: BALL_STYLES.pass.color, ball: "#FACC15", hi: "#FFFFFF" };
  return (
    <defs>
      {Object.entries(arrows).map(([k, c]) => (
        <marker key={k} id={`arr-${k}`} viewBox="0 0 10 10" refX="7" refY="5" markerWidth="1.6" markerHeight="1.6" markerUnits="userSpaceOnUse" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill={c} />
        </marker>
      ))}
    </defs>
  );
}

function TeeCap({ path, color, w }) {
  const b = path[path.length - 1], a = path[path.length - 2] || b;
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const nx = -(b.y - a.y) / len, ny = (b.x - a.x) / len;
  return <line x1={b.x + nx} y1={b.y + ny} x2={b.x - nx} y2={b.y - ny} stroke={color} strokeWidth={w * 1.3} strokeLinecap="round" />;
}

function RouteLine({ play, route, faded, strong }) {
  const st = ROUTE_STYLES[route.type];
  const { path: wp, samples } = routeTiming(play, route);
  if (wp.length < 2) return null;
  const path = [wp[0], ...samples.flatMap((s) => s.slice(1))];
  const w = strong ? 0.55 : 0.35;
  return (
    <g opacity={faded ? 0.2 : 1} data-testid={`route-line-${route.id}`}>
      <polyline points={ptsStr(path)} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth={w + 0.3} strokeLinejoin="round" />
      <polyline points={ptsStr(path)} fill="none" stroke={st.color} strokeWidth={w} strokeDasharray={st.dash} strokeLinecap="round" strokeLinejoin="round" markerEnd={st.end === "arrow" ? `url(#arr-${route.type})` : undefined} />
      {st.end === "tee" && <TeeCap path={path} color={st.color} w={w} />}
    </g>
  );
}

function BallLines({ play, highlightId }) {
  return sortedEvents(play).map((e) => {
    const f = playerById(play, e.from_id), to = playerById(play, e.to_id);
    if (!f || !to) return null;
    const a = playerPosAt(play, f, e.time), b = playerPosAt(play, to, e.time + e.duration);
    const st = BALL_STYLES[e.type];
    const faded = highlightId && e.from_id !== highlightId && e.to_id !== highlightId;
    return (
      <line key={e.id} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={st.color} strokeWidth={e.type === "pass" ? 0.35 : 0.25} strokeDasharray={st.dash} opacity={faded ? 0.2 : 0.95} markerEnd={`url(#arr-${e.type === "pass" ? "pass" : "ball"})`} />
    );
  });
}

function PlayerMarker({ p, pos, fill, selected, highlighted, faded, picked, onDown }) {
  const fs = p.label.length > 2 ? 1.0 : 1.25;
  const txt = textOn(fill);
  return (
    <g transform={`translate(${pos.x},${pos.y})`} opacity={faded ? 0.3 : 1} onPointerDown={onDown} style={{ cursor: onDown ? "pointer" : "default" }} data-testid={`player-marker-${p.label}`}>
      {(highlighted || selected || picked) && <circle r={3} fill="none" stroke={picked ? "#FACC15" : "#FF6B00"} strokeWidth={0.4} className={highlighted ? "tpc-pulse" : ""} />}
      <circle r={2.8} fill="transparent" />
      {p.side === "offense" ? (
        <circle r={1.75} fill={fill} stroke="#FFFFFF" strokeWidth={0.28} />
      ) : (
        <polygon points="0,-2.2 2.2,0 0,2.2 -2.2,0" fill={fill} stroke="#FFFFFF" strokeWidth={0.28} />
      )}
      <text y={0.08} fontSize={fs} fontWeight={800} fill={txt} textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none" }}>{p.label}</text>
      {p.number && (
        <text x={1.6} y={-1.9} fontSize={0.95} fontWeight={800} fill="#FFFFFF" stroke="#0B1528" strokeWidth={0.25} paintOrder="stroke" style={{ pointerEvents: "none" }}>#{p.number}</text>
      )}
      {p.nickname && (
        <text y={3.3} fontSize={0.95} fontWeight={700} fill="#FFFFFF" stroke="#0B1528" strokeWidth={0.25} paintOrder="stroke" textAnchor="middle" style={{ pointerEvents: "none" }}>{p.nickname}</text>
      )}
    </g>
  );
}

function Ball({ b }) {
  if (!b) return null;
  return (
    <g style={{ pointerEvents: "none" }} data-testid="ball-marker">
      {b.lift > 0 && <ellipse cx={b.x} cy={b.y} rx={0.6} ry={0.3} fill="rgba(0,0,0,0.35)" />}
      <g transform={`translate(${b.x},${b.y - b.lift * 2})`}>
        <ellipse rx={0.85} ry={0.52} fill="#8B4513" stroke="#FFFFFF" strokeWidth={0.12} />
        <line x1={-0.35} x2={0.35} y1={0} y2={0} stroke="#FFFFFF" strokeWidth={0.1} />
      </g>
    </g>
  );
}

export function PlayCanvas({ play, team, t = 0, showRoutes = true, highlightId, selectedId, pickedId, editRouteFor, activeRouteId, selPoint, onInsert, quizTarget, tapMark, onPlayerDown, onPointDown, onFieldDown, onMove, onUp, className = "w-full h-auto" }) {
  const ref = useRef(null);
  const sport = getSport(play.sport);
  const win = sport.viewWindow(play.los_y);
  const us = team?.colors?.primary || "#FF6B00";
  const toPt = (e) => {
    const svg = ref.current, pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };
  const capture = (e) => { try { ref.current.setPointerCapture(e.pointerId); } catch (_) { /* noop */ } };
  const ball = ballAt(play, t);
  const editRoutes = editRouteFor ? play.routes.filter((r) => r.player_id === editRouteFor) : [];
  const active = onInsert && play.routes.find((r) => r.id === activeRouteId && r.player_id === editRouteFor);
  const activePath = active ? routeTiming(play, active).path : [];

  return (
    <svg ref={ref} viewBox={`${win.x} ${win.y} ${win.w} ${win.h}`} className={`block select-none ${className}`} style={{ touchAction: onFieldDown || onPlayerDown ? "none" : "auto" }}
      onPointerDown={onFieldDown ? (e) => { capture(e); onFieldDown(toPt(e), e); } : undefined}
      onPointerMove={onMove ? (e) => onMove(toPt(e), e) : undefined}
      onPointerUp={onUp ? (e) => onUp(toPt(e), e) : undefined}
      onPointerCancel={onUp ? (e) => onUp(toPt(e), e) : undefined}
      data-testid="field-svg-canvas" role="img" aria-label={`Play diagram: ${play.name}`}>
      <RouteDefs />
      <sport.Field win={win} los={play.los_y} firstDown={play.first_down_y} />
      {showRoutes && play.routes.map((r) => <RouteLine key={r.id} play={play} route={r} faded={highlightId && r.player_id !== highlightId} strong={r.player_id === highlightId || r.id === activeRouteId} />)}
      {showRoutes && <BallLines play={play} highlightId={highlightId} />}
      {quizTarget && <circle cx={quizTarget.x} cy={quizTarget.y} r={quizTarget.r} fill="rgba(250,204,21,0.25)" stroke="#FACC15" strokeWidth={0.3} strokeDasharray="0.8 0.5" data-testid="quiz-target-area" />}
      {play.players.map((p) => (
        <PlayerMarker key={p.id} p={p} pos={playerPosAt(play, p, t)} fill={p.side === "offense" ? us : "#334155"}
          selected={p.id === selectedId} highlighted={p.id === highlightId} picked={p.id === pickedId} faded={highlightId && p.id !== highlightId}
          onDown={onPlayerDown ? (e) => { e.stopPropagation(); capture(e); onPlayerDown(p.id, toPt(e), e); } : undefined} />
      ))}
      {activePath.slice(1).map((b, i) => {
        const a = activePath[i];
        return (
          <g key={`ins-${i}`} onPointerDown={(e) => { e.stopPropagation(); onInsert(active.id, i); }} data-testid={`route-insert-${i}`} style={{ cursor: "copy" }}>
            <circle cx={(a.x + b.x) / 2} cy={(a.y + b.y) / 2} r={1.6} fill="transparent" />
            <circle cx={(a.x + b.x) / 2} cy={(a.y + b.y) / 2} r={0.6} fill="#0B1528" stroke="#FFFFFF" strokeWidth={0.15} />
            <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 + 0.05} fontSize={0.9} fontWeight={800} fill="#FFFFFF" textAnchor="middle" dominantBaseline="middle" style={{ pointerEvents: "none" }}>+</text>
          </g>
        );
      })}
      {editRoutes.map((r) => r.points.map((pt, i) => {
        const on = selPoint?.rid === r.id && selPoint?.idx === i;
        return (
          <g key={`${r.id}-${i}`} onPointerDown={(e) => { e.stopPropagation(); capture(e); onPointDown?.(r.id, i, toPt(e)); }} data-testid={`route-point-${r.id}-${i}`} style={{ cursor: "grab" }}>
            <circle cx={pt.x} cy={pt.y} r={2.4} fill="transparent" />
            <circle cx={pt.x} cy={pt.y} r={on ? 1.3 : 1} fill={on ? "#FACC15" : r.id === activeRouteId ? "#FF6B00" : "#FFFFFF"} stroke="#0B1528" strokeWidth={0.25} />
            {pt.pause > 0 && <text x={pt.x} y={pt.y - 1.7} fontSize={0.85} fontWeight={800} fill="#FACC15" stroke="#0B1528" strokeWidth={0.2} paintOrder="stroke" textAnchor="middle">{pt.pause}s</text>}
          </g>
        );
      }))}
      {tapMark && <circle cx={tapMark.x} cy={tapMark.y} r={0.9} fill={tapMark.ok ? "#22C55E" : "#FACC15"} stroke="#FFFFFF" strokeWidth={0.25} data-testid="quiz-tap-mark" />}
      <Ball b={ball} />
    </svg>
  );
}
